import React from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { searchPlugin } from '@react-pdf-viewer/search';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { Box, IconButton, Typography, Paper, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getApiUrl } from '../../config/appConfig';

// Importar estilos
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

interface PDFViewerProps {
  pdfUrl: string;
  onClose: () => void;
  title: string;
  documentId: string;
  documentType?: 'recibo' | 'pedido' | 'ajuste' | 'factura' | 'ordenPago' | 'nota-credito';
  downloadFileName?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, onClose, title, documentId, documentType = 'recibo', downloadFileName }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const searchPluginInstance = searchPlugin();
  const zoomPluginInstance = zoomPlugin();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = downloadFileName || `${title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShareWhatsApp = () => {
    // Determinar la URL pública según el tipo de documento
    const apiUrl = getApiUrl();
    let publicUrl = '';
    switch (documentType) {
      case 'pedido':
        publicUrl = `${apiUrl}/api/pedidosventa/${documentId}/pdf`;
        break;
      case 'ajuste':
        publicUrl = `${apiUrl}/api/inventario/ajustes/${documentId}/pdf`;
        break;
      case 'factura':
        publicUrl = `${apiUrl}/api/FacturasVenta/${documentId}/pdf`;
        break;
      case 'ordenPago':
        publicUrl = `${apiUrl}/api/tesoreria/orden-pago/${documentId}/pdf`;
        break;
      case 'nota-credito':
        publicUrl = `${apiUrl}/api/NotasCreditoVenta/${documentId}/pdf`;
        break;
      case 'recibo':
      default:
        publicUrl = `${apiUrl}/api/tesoreria/recibos/${documentId}/pdf`;
        break;
    }
    
    const message = encodeURIComponent(`Compartiendo ${title}\n\nPuedes descargar el documento aquí: ${publicUrl}`);
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        height: '90%',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 2,
        overflow: 'hidden',
        zIndex: 1300,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Compartir por WhatsApp">
            <IconButton onClick={handleShareWhatsApp} color="success">
              <WhatsAppIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100% - 64px)', overflow: 'hidden' }}>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer
            fileUrl={pdfUrl}
            plugins={[
              defaultLayoutPluginInstance,
              searchPluginInstance,
              zoomPluginInstance,
            ]}
            defaultScale={2}
            theme={{
              theme: 'light',
            }}
            onDocumentLoad={(e) => {
              console.log(`Documento cargado: ${e.doc.numPages} páginas`);
            }}
          />
        </Worker>
      </Box>
    </Paper>
  );
};

export default PDFViewer;
